import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../context/LanguageContext";
import { pickLocalizedName } from "../../utils/localizedName";
import "./clientTheme.css";
import "./StoreCategoryChips.css";

function ChevronDownIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function BackIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

export default function StoreCategoryChips({
  categories,
  categoryPath,
  selectedCategoryId,
  onSelectAll,
  onSelectCategory,
  onBack,
}) {
  const { t } = useTranslation(["storeCategoryChips", "common"]);
  const { language } = useLanguage();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  function updateScrollState() {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }

  useEffect(() => {
    updateScrollState();
    window.addEventListener("resize", updateScrollState);
    return () => window.removeEventListener("resize", updateScrollState);
  }, [categories, categoryPath]);

  if (!categories || categories.length === 0) return null;

  function scrollRow(direction) {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.7, behavior: "smooth" });
  }

  const currentParentId = categoryPath.length ? categoryPath[categoryPath.length - 1].categoryId : null;
  const visibleCategories = categories.filter((c) => c.parentCategoryId === currentParentId);
  const hasChildren = (category) => categories.some((c) => c.parentCategoryId === category.categoryId);
  const showingAll = selectedCategoryId === null && categoryPath.length === 0;

  return (
    <nav className="store-category-bar" aria-label={t("storeCategoriesNavLabel")}>
      {categoryPath.length > 0 && (
        <div className="store-category-breadcrumb">
          <button type="button" className="store-category-back" onClick={onBack}>
            <BackIcon /> {t("common:back")}
          </button>
          <span className="store-category-breadcrumb-text">
            {categoryPath
              .map((c) => pickLocalizedName(language, c.categoryName, c.categoryNameArabic, c.categoryNameHebrew))
              .join(" › ")}
          </span>
        </div>
      )}

      <div className="store-category-row">
        <div className="store-category-scroll" ref={scrollRef} onScroll={updateScrollState}>
          <ul className="store-category-list">
            <li className="store-category-item">
              <button
                type="button"
                className={`store-category-link ${showingAll ? "active" : ""}`}
                onClick={onSelectAll}
              >
                {t("all")}
              </button>
            </li>
            {visibleCategories.map((category) => {
              const isLeaf = !hasChildren(category);
              const isActive = isLeaf && selectedCategoryId === category.categoryId;
              return (
                <li className="store-category-item" key={category.categoryId}>
                  <button
                    type="button"
                    className={`store-category-link ${isActive ? "active" : ""}`}
                    onClick={() => onSelectCategory(category, isLeaf)}
                  >
                    {pickLocalizedName(language, category.categoryName, category.categoryNameArabic, category.categoryNameHebrew)}
                    {!isLeaf && <ChevronDownIcon />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {(canScrollLeft || canScrollRight) && (
          <div className="store-category-arrows">
            <button
              type="button"
              className="store-category-arrow"
              onClick={() => scrollRow(-1)}
              disabled={!canScrollLeft}
              aria-label={t("showPreviousCategories")}
            >
              <ArrowLeftIcon />
            </button>
            <button
              type="button"
              className="store-category-arrow"
              onClick={() => scrollRow(1)}
              disabled={!canScrollRight}
              aria-label={t("showMoreCategories")}
            >
              <ArrowRightIcon />
            </button>
          </div>
        )}
      </div>
    </nav>
  );
}
